import { VStack } from "@chakra-ui/react";
import { retryEnhancer } from "@rpldy/retry-hooks";
import { BatchItem } from "@rpldy/shared";
import { UploaderEnhancer } from "@rpldy/uploader";
import Uploady, { composeEnhancers } from "@rpldy/uploady";
import { DocumentTypeEnum } from "gen/model/documentTypeEnum";
import React, { useMemo } from "react";
import { Accept } from "react-dropzone";
import {
  FileUploadController,
  FileUploadHandle,
} from "shared/components/upload/FileUploadController";
import { getDocumentEnhancer } from "shared/components/upload/FileUploadDocumentEnhancer";
import { FileUploadDropZone } from "shared/components/upload/FileUploadDropZone";
import { FileUploadList } from "shared/components/upload/FileUploadList";

export type FileUploadItemFilter = (item: BatchItem) => boolean;

export interface FileUploadProps {
  accept?: Accept;
  autoUpload?: boolean;
  canUpload?: boolean;
  uploadEnhancer?: UploaderEnhancer;
  documentType?: DocumentTypeEnum;
  onItemsChanged: (files: BatchItem[]) => void;
  onItemFinished?: (file: BatchItem) => void;
  handleRef?: React.Ref<FileUploadHandle>;
  itemFilter?: FileUploadItemFilter;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  uploadEnhancer,
  documentType,
  onItemsChanged,
  onItemFinished,
  accept,
  autoUpload = true,
  canUpload = true,
  handleRef,
  itemFilter,
}) => {
  const mergedEnhancer = useMemo(() => {
    const enhancers = [retryEnhancer];
    if (documentType) {
      enhancers.push(getDocumentEnhancer(documentType));
    }
    if (uploadEnhancer) {
      enhancers.push(uploadEnhancer);
    }
    return enhancers.reduce(
      (acc, enhancer) => composeEnhancers(acc, enhancer),
      composeEnhancers(),
    );
  }, [documentType, uploadEnhancer]);

  return (
    <Uploady enhancer={mergedEnhancer} autoUpload={autoUpload}>
      <VStack alignItems="stretch" spacing="8px">
        <FileUploadController
          handleRef={handleRef}
          onItemsChanged={onItemsChanged}
          onItemFinished={onItemFinished}
        >
          {canUpload && <FileUploadDropZone options={{ accept }} />}
          <FileUploadList itemFilter={itemFilter} />
        </FileUploadController>
      </VStack>
    </Uploady>
  );
};
